<script>
export default {
  props: {
    // The primary key to distinguish different options
    primaryKey: {
      type: String,
      default: "id",
    },

    // The maximum number of options to select
    max: {
      type: Number,
      default: null,
    },

    // Size of the multiselect
    size: {
      type: String,
      default: "lg",
    },

    // The options for this multiselect
    options: {
      required: true,
      type: Object,
    },

    // Match function to determine if option matches search
    // (search, option) => { Boolean }
    match: {
      required: true,
      type: Function,
    },

    // The value selected by this multiselect.
    value: {
      type: Array,
      default() {
        return [];
      },
    },
  },

  data() {
    return {
      // The current search filter
      search: "",
    };
  },

  computed: {
    // The options that match the given filter
    filteredOptions() {
      let options = Object.values(this.options).filter((option) => {
        return this.match(this.search, option);
      });

      // Format as an object {id1: option1, id2: option2, ...}
      return Object.fromEntries(
        options.map((option) => {
          return [option[this.primaryKey], option];
        })
      );
    },

    // The options that are not selected
    unselectedOptions() {
      return Object.values(this.filteredOptions).filter((option) => {
        return !this.isSelected(option[this.primaryKey]);
      });
    },
  },

  methods: {
    // Select an option from the left at a given index
    selectOption(index) {
      // Do nothing if the maximum is reached
      if (this.max !== null && this.value.length == this.max) {
        return;
      }

      // Move item to this.value
      this.value.push(this.unselectedOptions[index]);

      // If the user selects all the options, clear out the search
      if (this.unselectedOptions.length == 0) {
        this.search = "";
      }

      this.emitUpdate();
    },

    // Unselect the option from the right with the given index
    unselectOption(index) {
      // Remove item from this.value
      this.value.splice(index, 1);

      // Emit an update that the value has changed
      this.emitUpdate();
    },

    // Emit an update to the model
    emitUpdate() {
      this.$emit("input", this.value);
    },

    // Determine if the given ID is selected
    isSelected(id) {
      return (
        this.value.find((value) => value[this.primaryKey] === id) !== undefined
      );
    },

    getInnerHeight(elm) {
      var computed = getComputedStyle(elm),
        padding =
          parseInt(computed.paddingTop) + parseInt(computed.paddingBottom);

      return elm.clientHeight - padding;
    },

    handleScroll() {
      var element = document.getElementById("div1");
      if (
        element.scrollTop + element.clientHeight >
        element.scrollHeight - element.clientHeight
      ) {
        this.$emit("getMorePosts");
      }
    },

    // Make the search input have active focus
    setFocus() {
      this.$refs.searchInput.focus();
    },
  },
};
</script>

<template>
  <b-row class="Container" @click="setFocus">
    <b-col md class="mb-3 mb-md-0 pr-md-2">
      <b-card class="Column" :size="size">
        <div class="Column-Header">
          <input
            class="form-control"
            ref="searchInput"
            v-model="search"
            placeholder="Search"
          />
        </div>
        <div class="Column-Body" id="div1" v-on:scroll="handleScroll">
          <b-col
            class="Row"
            v-for="(option, index) in unselectedOptions"
            :key="index"
            :ref="'option' + index"
            @click="selectOption(index)"
          >
            <slot name="format" :option="option">{{ option.name }}</slot>
          </b-col>
        </div>
      </b-card>
    </b-col>
    <b-col md class="pl-md-2">
      <b-card class="Column" :size="size">
        <div class="Column-Body">
          <b-col
            class="Row"
            v-for="(option, index) in value"
            :key="index"
            @click="unselectOption(index)"
          >
            <slot name="format" :option="option">{{ option.name }}</slot>
          </b-col>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<style scoped>
.Column {
  position: relative;
  overflow: hidden;
  padding: 0;
}

.Column[size="sm"] {
  height: 200px;
}

.Column[size="md"] {
  height: 250px;
}

.Column[size="lg"] {
  height: 300px;
}

.Column > .Column-Header {
  padding-top: 50px;
}

.Column-Header {
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  padding: 10px;
  border-bottom: 1px solid rgba(0, 20, 40, 0.1);
}

.Column-Body {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow-y: scroll;
}

.Column-Header ~ .Column-Body {
  top: 59px;
  height: calc(100% - 59px);
}

.Row {
  padding: 5px 10px;
}

.Row:hover {
  color: #007bff;
  background-color: rgba(0, 123, 255, 0.1);
}

.Row.active {
  color: #fff;
  text-decoration: none;
  background-color: #007bff;
}

.Column-Body .Row:last-child {
  border-bottom: none;
}
</style>
